import React from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import BioentitySearchInput from 'inventory/BioentitySearchInput';
import EntrySearchInput from 'notebook/entries/EntrySearchInput';
import BatchSearchInput from 'samples/batches/BatchSearchInput';
import StorableSearchInput from 'samples/storables/StorableSearchInput';
import {
  BASE_FILTER_FORM_PROP_NAMES,
  BaseFilterFormProps,
  FilterStyle,
} from 'search/filters/base/FilterFormConstants';
import OperatorDropdown from 'search/filters/base/OperatorDropdown';
import SecondaryFilterFormSkeleton from 'search/filters/base/SecondaryFilterFormSkeleton';
import {MentionableType, NoteReferenceFilter, Operator, SearchContext} from 'search/SearchConstants';
import {Item} from 'ui-library/attach-dropdown/ItemSearchInput';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import * as Keyboard from 'util/Keyboard';
import pickDefined from 'util/pickDefined';
import {assertNever} from 'util/Types';
import {WorklistType} from 'worklists/WorklistConstants';
import {WorklistMultiSearchInput} from 'worklists/WorklistSearchInput';

interface Props extends BaseFilterFormProps {
  filter: NoteReferenceFilter;
  filterStyle: FilterStyle;
  onChangeFilter: (filter: NoteReferenceFilter) => void;
  onEnter?: () => void;
  onRemove: () => void;
  operators: Array<Operator>;
}

function isMentionableTypeEnabled(type: MentionableType): boolean {
  if (type === MentionableType.BATCH && !BenchlingConfig.ENABLE_BATCHES) {
    return false;
  }
  return true;
}

export default class NoteReferenceFilterForm extends React.Component<Props> {
  handleChange = (items: Array<Item>): void => {
    const referencedIds = items.map((item) => item.id!);
    this.props.onChangeFilter({
      ...this.props.filter,
      value: {...this.props.filter.value, referencedIds},
    });
  };

  handleChangeReferencedType = (referencedType: MentionableType): void => {
    this.props.onChangeFilter({
      ...this.props.filter,
      value: {referencedType, referencedIds: []},
    });
  };

  inputForReferencedType(): JSX.Element {
    const selectedItems = this.props.filter.value.referencedIds.map((id) => ({id}));
    switch (this.props.filter.value.referencedType) {
      case MentionableType.BATCH:
        return (
          <BatchSearchInput
            onChange={this.handleChange}
            onKeyDown={Keyboard.callOnEnter(this.props.onEnter)}
            selectedBatches={selectedItems}
            shouldAllowMultiple={true}
            hideSelectedResults={true}
            context={SearchContext.LISTING}
          />
        );
      case MentionableType.BIOENTITY:
        return (
          <BioentitySearchInput
            onChange={this.handleChange}
            onKeyDown={Keyboard.callOnEnter(this.props.onEnter)}
            selectedItems={selectedItems}
            shouldAllowMultiple={true}
            hideSelectedResults={true}
            context={SearchContext.LISTING}
          />
        );
      case MentionableType.ENTRY:
        return (
          <EntrySearchInput
            onChange={this.handleChange}
            selectedEntries={selectedItems}
            shouldAllowMultiple={true}
            context={SearchContext.LISTING}
          />
        );
      case MentionableType.STORAGE:
        return (
          <StorableSearchInput
            onChange={this.handleChange}
            selectedStorables={selectedItems}
            shouldAllowMultiple={true}
            context={SearchContext.LISTING}
          />
        );
      case MentionableType.WORKLIST:
        return (
          <WorklistMultiSearchInput
            selectedItems={selectedItems}
            shouldAllowMultiple={true}
            onChange={this.handleChange}
            worklistTypes={Object.values(WorklistType)}
            context={SearchContext.LISTING}
          />
        );
      default:
        return assertNever(this.props.filter.value.referencedType);
    }
  }

  override render(): JSX.Element {
    const operatorDropdown = (
      <OperatorDropdown
        buttonSize={this.props.buttonSize}
        onChangeOperator={(operator: Operator) => {
          this.props.onChangeFilter({...this.props.filter, operator});
        }}
        operators={this.props.operators}
        selectedOperator={this.props.filter.operator}
      />
    );

    const input = this.inputForReferencedType();

    const typeDropdown = (
      <Dropdown
        menu={
          <Menu
            height={Menu.HEIGHTS.MEDIUM}
            isMulti={false}
            width={Menu.WIDTHS.FULL}
            value={this.props.filter.value.referencedType}
          >
            {Object.values(MentionableType)
              .filter((option) => isMentionableTypeEnabled(option))
              .map((option) => (
                <Option key={option} onSelect={this.handleChangeReferencedType} value={option}>
                  {option}
                </Option>
              ))}
          </Menu>
        }
      >
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          fullWidth={true}
          isDropdown={true}
          size={ButtonSize.SMALL}
          text={this.props.filter.value.referencedType}
        />
      </Dropdown>
    );

    assert(
      this.props.filterStyle === FilterStyle.SECONDARY,
      'NoteReference filter is only configured to work as a secondary filter'
    );

    const skeletonProps = pickDefined(this.props, ...BASE_FILTER_FORM_PROP_NAMES);
    return (
      <SecondaryFilterFormSkeleton
        {...skeletonProps}
        nodeGrid={[[operatorDropdown], [typeDropdown], [input]]}
        onChangeFilterKey={this.props.onChangeFilterKey}
        selectedFilterKey={this.props.selectedFilterKey}
        onRemove={this.props.onRemove}
      />
    );
  }
}
